import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';

/**
 * Журнал посещаемости детсадовской группы.
 *
 * Колонки = рабочие дни месяца по производственному календарю (отдаёт
 * бэкенд: Пн–Пт минус гос. выходные, плюс перенесённые рабочие субботы;
 * школьные каникулы для сада — рабочие). Семантика ячейки:
 *   • пустая        — был, без пометок (по умолчанию)
 *   • «Н»           — отсутствовал (presence=0)
 *   • «✓» с tooltip — был, есть опоздание/комментарий
 *
 * Внизу строка «Итого явка» (сколько детей пришло в этот день),
 * справа колонка «Итого» (сколько раз ребёнок был за месяц).
 *
 * Ввод: клик по ячейке фокусирует её, клавиша Н/н ставит «Н»,
 * Backspace очищает.
 */
const MONTHS_RU = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];

const URL_SCHEMA = { month: '' };

function pad(n) { return String(n).padStart(2, '0'); }
function todayMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function monthAdd(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function monthBounds(ym) {
  const [y, m] = ym.split('-').map(Number);
  const from = `${y}-${pad(m)}-01`;
  const last = new Date(y, m, 0);
  return { from, to: `${y}-${pad(m)}-${pad(last.getDate())}` };
}
// Фолбэк на случай старого ответа API без days: все Пн–Пт месяца.
function workingDaysOfMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const out = [];
  for (let d = 1; d <= last; d++) {
    const date = `${y}-${pad(m)}-${pad(d)}`;
    const wd   = (new Date(date + 'T00:00:00')).getDay(); // 0=Sun..6=Sat
    if (wd === 0 || wd === 6) continue;                   // выходные пропускаем
    out.push({ date, day: d, weekday: wd, note: null });
  }
  return out;
}

export default function KindergartenView({ classId, canWrite: canWriteProp }) {
  const can = useCan();
  // Админ определяет право через матрицу (can('journal','write')). Для
  // учителя-воспитателя это право всегда false, поэтому кабинет учителя
  // передаёт canWrite явным пропом (доступ уже проверил бэк —
  // KindergartenController::requireKgAccess по классруку/ассистенту).
  const canWrite = canWriteProp !== undefined ? canWriteProp : can('journal', 'write');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!params.month) setParams({ month: todayMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!classId || !params.month) { setData(null); return; }
    const { from, to } = monthBounds(params.month);
    setLoading(true); setError(null);
    api.get(`/api/kindergarten?class_id=${classId}&from=${from}&to=${to}`)
      .then((res) => setData(res))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, [classId, params.month]);

  const recordsByCell = useMemo(() => {
    const m = new Map();
    if (!data?.attendance) return m;
    for (const a of data.attendance) m.set(a.studentId + ':' + a.date, a);
    return m;
  }, [data]);

  const isEnrolledOn = (student, date) => {
    const list = student?.enrollments;
    if (!list || list.length === 0) return false;
    return list.some((e) => (!e.dateStart || e.dateStart <= date) && (!e.dateEnd || date <= e.dateEnd));
  };

  const monthYM = params.month || todayMonth();
  // Дни берём из ответа бэкенда (учёт производственного календаря:
  // без гос. выходных, с перенесёнными рабочими субботами).
  const days = useMemo(() => {
    if (data?.days) {
      return data.days.map((d) => ({
        date:    d.date,
        day:     Number(d.date.slice(8, 10)),
        weekday: (new Date(d.date + 'T00:00:00')).getDay(), // 0=Sun..6=Sat
        note:    d.note || null,
      }));
    }
    return workingDaysOfMonth(monthYM);
  }, [data, monthYM]);

  // Итог по ученику: сколько рабочих дней он был в группе. День считается
  // «был», если ученик зачислен на эту дату И нет явной отметки «Н».
  const studentTotals = useMemo(() => {
    const m = new Map();
    if (!data) return m;
    for (const st of data.students) {
      let present = 0;
      for (const d of days) {
        if (!isEnrolledOn(st, d.date)) continue;
        const a = recordsByCell.get(st.id + ':' + d.date);
        if (!a || a.present) present++;
      }
      m.set(st.id, present);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, days, recordsByCell]);

  // Итог по дню: сколько детей пришло в этот день (зачислены и нет «Н»).
  const dayTotals = useMemo(() => {
    const m = new Map();
    if (!data) return m;
    for (const d of days) {
      let present = 0;
      for (const st of data.students) {
        if (!isEnrolledOn(st, d.date)) continue;
        const a = recordsByCell.get(st.id + ':' + d.date);
        if (!a || a.present) present++;
      }
      m.set(d.date, present);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, days, recordsByCell]);

  const grandTotal = useMemo(() => {
    let s = 0;
    for (const v of studentTotals.values()) s += v;
    return s;
  }, [studentTotals]);

  // Неявок = количество явных «Н» в окне (только в рабочие дни и только когда
  // ребёнок реально числится в группе на эту дату).
  const absencesTotal = useMemo(() => {
    if (!data) return 0;
    let n = 0;
    const workDates = new Set(days.map((d) => d.date));
    for (const a of data.attendance) {
      if (a.present) continue;
      if (!workDates.has(a.date)) continue;
      const st = data.students.find((s) => s.id === a.studentId);
      if (!st || !isEnrolledOn(st, a.date)) continue;
      n++;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, days]);

  const sendUpsert = (student, date, payload) => {
    api.put('/api/kindergarten/attendance', {
      classId, studentId: student.id, date, ...payload,
    }).then((res) => {
      setData((prev) => {
        if (!prev) return prev;
        const a = res.attendance;
        let next = { ...prev };
        if (a === null) {
          next.attendance = prev.attendance.filter((x) =>
            !(x.studentId === student.id && x.date === date)
          );
        } else {
          const idx = prev.attendance.findIndex((x) => x.studentId === student.id && x.date === date);
          if (idx >= 0) {
            next.attendance = prev.attendance.map((x, i) => i === idx ? a : x);
          } else {
            next.attendance = [...prev.attendance, a];
          }
        }
        return next;
      });
    }).catch((err) => alert(err instanceof ApiError ? err.message : 'Не удалось сохранить'));
  };

  const onCellKeyDown = (student, date, e) => {
    if (!canWrite) return;
    if (!isEnrolledOn(student, date)) return;
    if (e.target !== e.currentTarget) return;
    const k = e.key;
    if (k === 'Н' || k === 'н' || k === 'H' || k === 'h') {
      e.preventDefault();
      sendUpsert(student, date, { present: 0, late: '', comment: '' });
    } else if (k === 'Delete' || k === 'Backspace') {
      e.preventDefault();
      sendUpsert(student, date, { present: 1, late: '', comment: '' });
    }
  };

  const goPrev  = () => setParams({ month: monthAdd(monthYM, -1) });
  const goNext  = () => setParams({ month: monthAdd(monthYM, 1) });
  const goToday = () => setParams({ month: todayMonth() });

  const onDownload = () => {
    const { from, to } = monthBounds(monthYM);
    // Сервер генерирует XLSX и отдаёт через Content-Disposition.
    // Пробрасываем сразу через window.location, чтобы открылся диалог сохранения.
    const url = `/api/kindergarten/export?class_id=${classId}&from=${from}&to=${to}`;
    window.location.assign(url);
  };

  return (
    <>
      <div className="jrv-toolbar jrv-toolbar--inline">
        <div className="jrv-period">
          <button className="jr-period-nav" onClick={goPrev} title="Предыдущий месяц"><Icon name="chevron" size={11} /></button>
          <span className="jr-period">
            {MONTHS_RU[Number(monthYM.slice(5, 7)) - 1]} {monthYM.slice(0, 4)}
          </span>
          <button className="jr-period-nav" onClick={goNext} title="Следующий месяц"><Icon name="chevron" size={11} /></button>
          <button className="jr-period-today" onClick={goToday}>Сегодня</button>
        </div>

        <div className="jrv-metrics">
          <div className="jrv-met"><span className="jrv-met__label">Явок</span><span className="jrv-met__value">{grandTotal}</span></div>
          <div className="jrv-met"><span className="jrv-met__label">Неявок</span><span className={'jrv-met__value' + (absencesTotal ? ' jr-met__value--bad' : '')}>{absencesTotal}</span></div>
        </div>

        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={onDownload}
          disabled={!data || data.students.length === 0}
          style={{ marginLeft: 'auto' }}
          title="Скачать таблицу за месяц в Excel"
        >
          <Icon name="download" size={12} /> Скачать xlsx
        </button>
      </div>

      <div className="jr-content">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {loading || !data ? (
          <div className="ref-empty">Загрузка…</div>
        ) : data.students.length === 0 ? (
          <div className="ref-empty">В группе нет детей за этот месяц.</div>
        ) : (
          <div className="jr-table-wrap jr-table-wrap--full">
            <table className="jr-table jr-table--kg">
              <thead>
                <tr>
                  <th className="jr-th-name">Ребёнок</th>
                  {days.map((d) => (
                    <th
                      key={d.date}
                      className={'jr-th-date' + (d.note ? ' is-transfer' : '')}
                      title={d.note ? `${d.date} — ${d.note}` : d.date}
                    >
                      <div className="jr-th-date__num">{pad(d.day)}</div>
                      <div className="jr-th-date__pos">
                        {['Вс','Пн','Вт','Ср','Чт','Пт','Сб'][d.weekday]}
                      </div>
                    </th>
                  ))}
                  <th className="jr-th-date jr-th-tri jr-th-tri--avg" title="Итого явок за месяц">
                    <div className="jr-th-tri__small">итог</div>
                    <div className="jr-th-tri__big">явка</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((st) => (
                  <tr key={st.id}>
                    <td className="jr-td-name">{st.fullName}</td>
                    {days.map((d) => {
                      const enrolled = isEnrolledOn(st, d.date);
                      const editable = enrolled && canWrite;
                      const a = recordsByCell.get(st.id + ':' + d.date);
                      const cellTitle = !enrolled
                        ? 'Не числится в группе на эту дату'
                        : (a?.comment || (a?.late ? 'Опоздание: ' + a.late : undefined));
                      return (
                        <td
                          key={d.date}
                          className={
                            'jr-td-cell' +
                            (editable ? ' is-editable' : '') +
                            (!enrolled ? ' is-disabled' : '')
                          }
                          tabIndex={editable ? 0 : -1}
                          onClick={(e) => editable && e.currentTarget.focus()}
                          onKeyDown={(e) => onCellKeyDown(st, d.date, e)}
                          title={cellTitle}
                        >
                          {a && <KgCell record={a} />}
                        </td>
                      );
                    })}
                    <td className="jr-td-cell jr-td-tri jr-td-tri--avg">
                      <span className="jr-cell jr-c-other">{studentTotals.get(st.id) ?? 0}</span>
                    </td>
                  </tr>
                ))}
                {/* Итог по дням */}
                <tr className="jr-tr-total">
                  <td className="jr-td-name jr-td-name--total">Итого явка</td>
                  {days.map((d) => (
                    <td key={d.date} className="jr-td-cell jr-td-cell--total">
                      <span className="jr-cell jr-c-other">{dayTotals.get(d.date) ?? 0}</span>
                    </td>
                  ))}
                  <td className="jr-td-cell jr-td-tri jr-td-tri--avg jr-td-cell--total">
                    <span className="jr-cell jr-c-other">{grandTotal}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function KgCell({ record }) {
  if (!record.present) return <span className="jr-cell jr-c-absent">Н</span>;
  return (
    <span className="jr-cell jr-c-other">
      ✓
      {record.late && <span className="jr-late-dot" title={'Опоздание: ' + record.late}>⏱</span>}
    </span>
  );
}
